import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { countryCodes } from "../constants/countryCodes";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { services } from "../mockData";
import { getGeoLocation, submitLead } from "../utils/api";

const Contact = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    countryCode: "+1",
    phone: "",
    service: "",
    message: "",
  });

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeoLocation = async () => {
      const geoResult = await getGeoLocation();
      if (geoResult.success) {
        setGeoData(geoResult);
        // Auto-set country code based on location
        setFormData((prev) => ({
          ...prev,
          countryCode: `+${geoResult.dialCode}`,
        }));
      }
    };

    fetchGeoLocation();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phone,
        countryDialCode: parseInt(formData.countryCode.replace("+", "")),
        country: geoData?.countryCode || "US",
        description: `${formData.service ? `Service: ${formData.service}. ` : ""}${formData.message}`,
        ipAddress: geoData?.ipAddress || "",
        pageUrl: window.location.href,
        city: geoData?.city || "",
        state: geoData?.state || "",
        geoLocation: geoData?.fullResponse || null,
        packageTitle: formData.service || "",
        packageAmount: 0,
      };

      const result = await submitLead(leadData);

      if (result.success) {
        toast.success(result.message || "Thank you! Redirecting...");
        setFormData({
          name: "",
          email: "",
          countryCode: "+1",
          phone: "",
          service: "",
          message: "",
        });

        // Redirect to thank you page after a short delay
        setTimeout(() => {
          navigate("/thank-you");
        }, 2000);
      } else {
        toast.error(
          result.message || "Something went wrong. Please try again.",
        );
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("Failed to submit form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special validation for phone - only numbers, max 10 digits
    if (name === "phone") {
      const numericValue = value.replace(/\D/g, ""); // Remove all non-digits
      if (numericValue.length <= 10) {
        setFormData({ ...formData, [name]: numericValue });
      }
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-amber-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Get In Touch
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Have questions about our services? Our team is here to help you
              protect your brand.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Send Us a Message
              </h2>
              <Card className="border-2 border-gray-200">
                <CardContent className="pt-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <div className="flex gap-2 mt-1">
                        <Select
                          value={formData.countryCode}
                          onValueChange={(value) =>
                            setFormData((prev) => ({
                              ...prev,
                              countryCode: value,
                            }))
                          }
                        >
                          <SelectTrigger className="w-28">
                            {countryCodes.find(
                              (c) => c.code === formData.countryCode,
                            )
                              ? `${countryCodes.find((c) => c.code === formData.countryCode).abbr} ${countryCodes.find((c) => c.code === formData.countryCode).code}`
                              : "US +1"}
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px] overflow-y-auto">
                            {countryCodes.map((item) => (
                              <SelectItem key={item.code} value={item.code}>
                                {item.abbr} {item.code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="1234567890"
                          maxLength={10}
                          pattern="[0-9]{10}"
                          title="Please enter exactly 10 digits"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="service">Service Interested In *</Label>
                      <Select
                        value={formData.service}
                        onValueChange={(value) =>
                          setFormData({ ...formData, service: value })
                        }
                        required
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.id}>
                              {service.title}
                            </SelectItem>
                          ))}
                          <SelectItem value="other">
                            Other / General Inquiry
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your needs..."
                        rows={5}
                        className="mt-1"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Contact Information
              </h2>
              <div className="space-y-6">
                <Card className="border-2 border-gray-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          Office Address
                        </h3>
                        <p className="text-gray-600">
                          11811 North Freeway Suite 222
                          <br />
                          Houston, TX 77060
                          <br />
                          United States
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-gray-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          Phone
                        </h3>
                        <p className="text-gray-600 mb-2">
                          Main: +1 (605) 269 5389
                        </p>
                        <p className="text-sm text-gray-500">
                          Monday - Friday, 9 AM - 6 PM EST
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-gray-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          Email
                        </h3>
                        <p className="text-gray-600">
                          General: info@trademarkupgrades.com
                          <br />
                          Support: support@trademarkupgrades.com
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      {/*<section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Visit Our Office</h2>
            <p className="text-lg text-gray-600">We're conveniently located in the heart of New York City</p>
          </div>
          <div className="bg-gray-300 h-96 rounded-xl flex items-center justify-center">
            <p className="text-gray-600 text-lg">Map Location Placeholder</p>
          </div>
        </div>
      </section>*/}

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Schedule consultation with our trademark experts today.
          </p>
          <Button
            size="lg"
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            Schedule Consultation
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Contact;
